// The live transcript beside a call's page in Blueprint (calls round): LiveTranscript fed by
// calls.liveTranscript {callId, after}, read when the page opens, again each time the office stream's
// call_transcript.lines_added names the call, and every POLL_MS while it's live with no word from the
// stream (a fallback, should the stream be down). Final lines are added in order; what's still being
// said shows as the line in progress until its final replaces it (live-transcript-model.ts). After the
// call, the saved transcript shows, as the server allows (a quarter hour for the office, AD-9).
import { useEffect, useRef, useState } from 'react';
import { LiveTranscript } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { onLiveChange } from '../connection/live.ts';
import type { Call } from '../types.ts';
import {
  NOTHING_HELD,
  POLL_MS,
  TRANSCRIPT_EVENTS,
  drawnLines,
  peopleOf,
  takeRead,
  transcriptStatus,
  type LiveHeld,
  type LiveRead,
} from './live-transcript-model.ts';

export function useLiveTranscript(callId: string, callRunning: boolean): LiveHeld {
  const [held, setHeld] = useState<LiveHeld>(NOTHING_HELD);
  const had = useRef<LiveHeld>(NOTHING_HELD);
  const heard = useRef(0); // when the stream last named this call
  const reading = useRef<Promise<void> | null>(null);
  const again = useRef(false);
  const read = useRef<() => void>(() => undefined);

  useEffect(() => {
    let live = true;
    had.current = NOTHING_HELD;
    setHeld(NOTHING_HELD);
    // one read at a time; a word that comes during one reads once more after it
    read.current = () => {
      if (reading.current) {
        again.current = true;
        return;
      }
      reading.current = (async () => {
        try {
          for (let pages = 0; pages < 20; pages++) {
            const r = await client.call<LiveRead>('calls.liveTranscript', {
              callId,
              ...(had.current.next ? { after: had.current.next } : {}),
            });
            if (!live) return;
            had.current = takeRead(had.current, r);
            setHeld(had.current);
            if (!r.more) break;
          }
        } catch {
          // kept as it was: the next word or poll reads again
        } finally {
          reading.current = null;
          if (live && again.current) {
            again.current = false;
            read.current();
          }
        }
      })();
    };
    read.current();
    const stop = onLiveChange(({ type, entityId }) => {
      if (entityId !== callId || !TRANSCRIPT_EVENTS.test(type)) return;
      heard.current = Date.now();
      read.current();
    });
    return () => {
      live = false;
      stop();
    };
  }, [callId]);

  // the fallback: while it's live (or the call runs), read when the stream has said nothing for a while
  const polling = held.live === true || callRunning;
  useEffect(() => {
    if (!polling) return;
    const t = setInterval(() => {
      if (Date.now() - heard.current >= POLL_MS) read.current();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [polling, callId]);

  // the call just ended: read once more for the saved lines
  const wasRunning = useRef(callRunning);
  useEffect(() => {
    if (wasRunning.current && !callRunning) read.current();
    wasRunning.current = callRunning;
  }, [callRunning]);

  return held;
}

export function CallLiveTranscript({ call }: { call: Call }) {
  const running = call.status === 'ringing' || call.status === 'in_progress';
  const held = useLiveTranscript(call.id, running);
  const people = peopleOf(held.lines);
  return (
    <LiveTranscript
      status={transcriptStatus(held, running)}
      lines={drawnLines(held.lines)}
      partial={drawnLines(held.partial)}
      {...(people ? { people } : {})}
    />
  );
}
